import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { isRecapViewer, RECAP_ACCESS_LEVELS } from 'features/recaps/constants'
import {
  applyExternalRecapSharingGate,
  buildAccessListOrganization,
  countShareRecipients,
  enrichAccessListUserGroups,
  enrichAccessListUsers,
  getExternalPermissionRows,
  getInternalPermissionTuples,
  getPendingInviteRows,
  mapExternalUsersToAccessListRows,
  mapPendingInvitesToAccessListRows,
  mergeDeletePermissionsIntoAccessList,
  mergePostPermissionsIntoAccessList,
  normalizeRecapPermissionsResponse,
  splitRecapPermissionTuples,
} from 'features/recaps/modals/share_modal/utils'
import { useLazyFetchRecapPermissionsQuery } from 'hooks/notetaker/api'

import { initialAccessListState } from './constants'

// Resolve permission tuples into owner, user, group, org, external, and pending rows.
const loadEnrichedAccessList = async ({
  dispatch,
  normalizedPermissions,
  owner,
  currentUser,
  orgUuid,
  hasExternalRecapSharing,
}) => {
  const internalTuples = getInternalPermissionTuples(normalizedPermissions)
  const externalRows = hasExternalRecapSharing
    ? getExternalPermissionRows(normalizedPermissions)
    : []
  const pendingRows = hasExternalRecapSharing
    ? getPendingInviteRows(normalizedPermissions)
    : []
  const { users, userGroups, organizations } = splitRecapPermissionTuples(internalTuples)
  const { owner: accessListOwner, users: enrichedUsers } = await enrichAccessListUsers(
    dispatch,
    users,
    owner,
    currentUser,
  )
  const enrichedUserGroups = await enrichAccessListUserGroups(userGroups)

  return {
    owner: accessListOwner,
    internalUsers: enrichedUsers,
    userGroups: enrichedUserGroups,
    organization: buildAccessListOrganization(orgUuid, organizations),
    externalUsers: hasExternalRecapSharing
      ? mapExternalUsersToAccessListRows(externalRows)
      : [],
    pendingInvites: hasExternalRecapSharing
      ? mapPendingInvitesToAccessListRows(pendingRows)
      : [],
  }
}

const countRestrictedRecipients = ({
  permissionUsers,
  enrichedAccessList,
  ownerUserUuid,
}) => countShareRecipients(
  permissionUsers,
  enrichedAccessList.userGroups,
  ownerUserUuid,
  {
    externalUsers: enrichedAccessList.externalUsers,
    pendingInvites: enrichedAccessList.pendingInvites,
  },
)

// Recipient rows and recipient count only shown when access level is Restricted.
const applyAccessListState = ({
  enrichedAccessList,
  permissionUsers,
  accessLevel,
  ownerUserUuid,
  setRecipientCount,
  setAccessList,
  isError = false,
}) => {
  const isRestricted = accessLevel === RECAP_ACCESS_LEVELS.RESTRICTED

  setRecipientCount(isRestricted
    ? countRestrictedRecipients({ permissionUsers, enrichedAccessList, ownerUserUuid })
    : 0)

  setAccessList({
    owner: enrichedAccessList.owner,
    organization: enrichedAccessList.organization,
    internalUsers: isRestricted ? enrichedAccessList.internalUsers : [],
    userGroups: isRestricted ? enrichedAccessList.userGroups : [],
    externalUsers: isRestricted ? enrichedAccessList.externalUsers : [],
    pendingInvites: isRestricted ? enrichedAccessList.pendingInvites : [],
    isLoading: false,
    isFetched: true,
    isError,
  })
}

export const useAccessList = ({
  isShareModalOpen,
  accessLevel,
  trackedMeetingUuid,
  recapPermission,
  owner,
  currentUser,
  orgUuid,
  hasExternalRecapSharing = false,
}) => {
  const dispatch = useDispatch()
  const [accessList, setAccessList] = useState(initialAccessListState)
  const [recipientCount, setRecipientCount] = useState(0)
  const [permissionsResult, setPermissionsResult] = useState(null)
  const [resolvedAccessList, setResolvedAccessList] = useState(null)
  const [refetchNonce, setRefetchNonce] = useState(0)
  const lastPermissionsResultRef = useRef(null)
  const [fetchPermissions] = useLazyFetchRecapPermissionsQuery()
  const ownerUuid = owner?.uuid

  const refetch = useCallback(() => {
    setRefetchNonce((nonce) => nonce + 1)
  }, [])

  const applyPostMutationResult = useCallback((postResponse, recipientsAtSubmit) => {
    setAccessList((current) => {
      const mergedAccessList = mergePostPermissionsIntoAccessList(current, postResponse, recipientsAtSubmit)

      setRecipientCount(countShareRecipients(
        mergedAccessList.internalUsers,
        mergedAccessList.userGroups,
        ownerUuid,
        {
          externalUsers: mergedAccessList.externalUsers,
          pendingInvites: mergedAccessList.pendingInvites,
        },
      ))

      return mergedAccessList
    })
  }, [ownerUuid])

  const applyDeleteMutationResult = useCallback((deletePayload) => {
    setAccessList((current) => {
      const mergedAccessList = mergeDeletePermissionsIntoAccessList(current, deletePayload)

      setRecipientCount(countShareRecipients(
        mergedAccessList.internalUsers,
        mergedAccessList.userGroups,
        ownerUuid,
        {
          externalUsers: mergedAccessList.externalUsers,
          pendingInvites: mergedAccessList.pendingInvites,
        },
      ))

      return mergedAccessList
    })
  }, [ownerUuid])

  const updatePermissionsResult = useCallback((nextPermissionsResult) => {
    const lastPermissionsResult = lastPermissionsResultRef.current

    if (
      lastPermissionsResult != null
      && lastPermissionsResult.isError === nextPermissionsResult.isError
      && lastPermissionsResult.normalizedPermissions === nextPermissionsResult.normalizedPermissions
    ) {
      return
    }

    lastPermissionsResultRef.current = nextPermissionsResult
    setPermissionsResult(nextPermissionsResult)
  }, [])

  // Start the permissions query when the modal opens and sharing is allowed.
  useEffect(() => {
    if (!isShareModalOpen || isRecapViewer(recapPermission) || !trackedMeetingUuid) {
      return undefined
    }

    let isCancelled = false

    const loadPermissions = async () => {
      setPermissionsResult(null)
      setResolvedAccessList(null)
      // this prevent infinite loop on open
      lastPermissionsResultRef.current = null
      setAccessList((current) => ({
        ...current,
        isLoading: true,
        isFetched: false,
        isError: false,
      }))

      try {
        // Intentionally do not pass preferCacheValue=true. A valid RTK cache hit is fine,
        // but modal open should not explicitly prefer a stale cache entry over a request.
        const fetchedPermissions = await fetchPermissions(trackedMeetingUuid).unwrap()

        if (isCancelled) return

        const normalizedPermissions = applyExternalRecapSharingGate(
          normalizeRecapPermissionsResponse(fetchedPermissions),
          hasExternalRecapSharing,
        )

        updatePermissionsResult({
          isError: false,
          normalizedPermissions,
        })
      } catch {
        if (isCancelled) return

        updatePermissionsResult({
          isError: true,
          normalizedPermissions: null,
        })
      }
    }

    loadPermissions()

    return () => {
      isCancelled = true
    }
  }, [
    fetchPermissions,
    hasExternalRecapSharing,
    isShareModalOpen,
    recapPermission,
    refetchNonce,
    trackedMeetingUuid,
    updatePermissionsResult,
  ])

  useEffect(() => {
    if (!isShareModalOpen || !permissionsResult) {
      return undefined
    }

    let isCancelled = false

    const resolveAccessList = async () => {
      try {
        const normalizedPermissions = permissionsResult.isError
          ? null
          : permissionsResult.normalizedPermissions
        const { users } = splitRecapPermissionTuples(getInternalPermissionTuples(normalizedPermissions))

        const enrichedAccessList = await loadEnrichedAccessList({
          dispatch,
          normalizedPermissions,
          owner,
          currentUser,
          orgUuid,
          hasExternalRecapSharing,
        })

        if (isCancelled) return

        setResolvedAccessList({
          enrichedAccessList,
          permissionUsers: users,
          isError: permissionsResult.isError,
        })
      } catch {
        if (isCancelled) return

        try {
          const fallbackAccessList = await loadEnrichedAccessList({
            dispatch,
            normalizedPermissions: null,
            owner,
            currentUser,
            orgUuid,
            hasExternalRecapSharing,
          })

          if (isCancelled) return

          setResolvedAccessList({
            enrichedAccessList: fallbackAccessList,
            permissionUsers: [],
            isError: true,
          })
        } catch {
          if (isCancelled) return

          setAccessList((current) => ({
            ...current,
            isLoading: false,
            isFetched: true,
            isError: true,
          }))
        }
      }
    }

    resolveAccessList()

    return () => {
      isCancelled = true
    }
  }, [
    currentUser,
    dispatch,
    hasExternalRecapSharing,
    isShareModalOpen,
    orgUuid,
    ownerUuid,
    permissionsResult,
    owner,
  ])

  useEffect(() => {
    if (!isShareModalOpen || !resolvedAccessList) return

    applyAccessListState({
      enrichedAccessList: resolvedAccessList.enrichedAccessList,
      permissionUsers: resolvedAccessList.permissionUsers,
      accessLevel,
      ownerUserUuid: ownerUuid,
      setRecipientCount,
      setAccessList,
      isError: resolvedAccessList.isError,
    })
  }, [accessLevel, isShareModalOpen, ownerUuid, resolvedAccessList])

  useEffect(() => {
    if (!isShareModalOpen) {
      setAccessList(initialAccessListState)
      setRecipientCount(0)
      setPermissionsResult(null)
      setResolvedAccessList(null)
      lastPermissionsResultRef.current = null
    }
  }, [isShareModalOpen])

  // Update recipient count and access list when access level changes.
  useEffect(() => {
    if (!isShareModalOpen || accessLevel == null) return

    if (accessLevel !== RECAP_ACCESS_LEVELS.RESTRICTED) {
      setRecipientCount(0)
      setAccessList((current) => ({
        ...current,
        internalUsers: [],
        userGroups: [],
        externalUsers: [],
        pendingInvites: [],
      }))
    }
  }, [accessLevel, isShareModalOpen])

  return {
    accessList,
    recipientCount,
    setRecipientCount,
    refetch,
    applyPostMutationResult,
    applyDeleteMutationResult,
  }
}
